import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { YearStatus } from "@/components/YearSelector/YearSelector";
import { ImpactDataset } from "@/hooks/useEcoInventDatasets";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { filterSearchToGetFilter, GetData, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Material } from "./MaterialOverview/MaterialsOverview";

export interface GetMaterialsApi {
  total_count: number;
  complete_count?: number;
  incomplete_count?: number;
  out_of_scope_count?: number;
  filter_options?: Filter;
  materials: Material[];
  status: "success" | "failed";
}

export interface GetMaterials extends GetData<Material> {}

export function useGetMaterials({
  year,
  showUnrelated = false,
  pagination,
  filter,
  search,
  sort,
  enabled,
}: {
  year?: number;
  showUnrelated?: boolean;
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  enabled?: boolean;
}) {
  const yearParam = year ? { year } : {};
  const showUnrelatedParam = showUnrelated ? { show_unrelated: showUnrelated } : {};

  return useQuery({
    queryKey: ["materials", year, showUnrelated, pagination, filter, search, sort],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetMaterialsApi>("/materials", {
          params: {
            ...yearParam,
            ...showUnrelatedParam,
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
          },
        })
        .then((result) => ({ ...result.data, data: result.data.materials }) as GetMaterials),
  });
}
export interface MaterialDetail extends Material {
  match?: ImpactDataset;
  supplier_address_is_valid?: boolean;
}

export function useGetMaterial({ materialId, enabled }: { materialId: number; enabled?: boolean }) {
  return useQuery({
    queryKey: ["material", materialId, "details"],
    enabled: enabled ?? true,
    queryFn: () =>
      axios.get<MaterialDetail>("/materials/" + materialId).then((result) => result.data),
  });
}

export interface MaterialPatch {
  name?: string;
  impact_dataset_id?: number;
  clear?: "impact_dataset_id"[];
}

export function usePatchMaterial() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ materialId, patch }: { materialId: number; patch: MaterialPatch }) =>
      axios.patch("/materials/" + materialId, patch),
    onSuccess: (_, { materialId }) => {
      smartQueryClient.invalidateQueries(["materials"]);
      smartQueryClient.invalidateQueries(["dashboard"]);
    },
    onMutate: ({ materialId, patch }) => {
      smartQueryClient.setQueryData<Material>(["material", materialId, "details"], (data) => {
        if (!data) return undefined;
        return { ...data, ...patch };
      });
    },
  });
}

export function useBulkPatchMaterial() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ materialIds, patch }: { materialIds: number[]; patch: MaterialPatch }) =>
      axios.patch("/materials?ids=" + materialIds.join(";"), patch),
    onSuccess: (_, { materialIds }) => {
      smartQueryClient.invalidateQueries(["materials"]);
      smartQueryClient.invalidateQueries(["dashboard"]);
      materialIds.map((materialId) => smartQueryClient.invalidateQueries(["material", materialId]));
    },
  });
}

export interface RelatedProduct {
  id: number;
  external_id: string;
  name: string;
  relation: "proxy" | "primary_source";
  unrelated: boolean;
}

export interface GetMaterialRelatedProducts {
  related_products: RelatedProduct[];
  total_count: number;
}

export function useGetMaterialRelatedProducts({
  materialId,
  pagination,
}: {
  materialId: number;
  pagination: Pagination;
}) {
  return useQuery({
    queryKey: ["material", materialId, "related_products", pagination],
    keepPreviousData: true,
    queryFn: () =>
      axios
        .get<GetMaterialRelatedProducts>("/materials/" + materialId + "/related_products", {
          params: { page: pagination.page, page_size: pagination.pageSize },
        })
        .then((result) => result.data),
  });
}

export function useDeleteMaterials() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ materialIds }: { materialIds: number[] }) =>
      axios.delete("/materials", {
        params: {
          ids: materialIds.join(";"),
        },
      }),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["materials"]);
      smartQueryClient.invalidateQueries(["products"]);
    },
  });
}

export interface MaterialUnit {
  start_unit: string;
  target_unit: string;
  conversion_factor: number | null;
  status: "complete" | "incomplete" | "validating";
}

export type GetMaterialUnits = MaterialUnit[];

export function useGetMaterialUnits({ materialId }: { materialId: number }) {
  return useQuery({
    queryKey: ["material", materialId, "units"],
    keepPreviousData: true,
    queryFn: () =>
      axios
        .get<GetMaterialUnits>("/materials/" + materialId + "/units")
        .then((result) => result.data),
  });
}

export interface UpdateMaterialUnit {
  start_unit: string;
  target_unit: string;
  factor: number | null;
}

export function useUpdateMaterialUnit() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({
      materialId,
      materialUnit,
    }: {
      materialId: number;
      materialUnit: UpdateMaterialUnit;
    }) =>
      axios.post<{ new_status: "complete" | "incomplete" }>(
        "/materials/" + materialId + "/units",
        materialUnit
      ),
    onSuccess: ({ data: { new_status: newStatus } }, { materialId, materialUnit }) => {
      smartQueryClient.invalidateQueries(["dashboard"]);
      smartQueryClient.invalidateQueries(["materials"]);
      smartQueryClient.invalidateQueries(["material", materialId, "details"]);

      smartQueryClient.setQueryData<GetMaterialUnits>(
        ["material", materialId, "units"],
        (materialUnits) => {
          return materialUnits
            ? materialUnits.map((unit) => {
                if (
                  unit.start_unit === materialUnit.start_unit &&
                  unit.target_unit === materialUnit.target_unit
                ) {
                  return {
                    ...unit,
                    status: newStatus,
                  };
                } else {
                  return unit;
                }
              })
            : undefined;
        }
      );
    },
  });
}

export function useMaterialStatusPerYear() {
  return useQuery({
    queryKey: ["materials", "years"],
    keepPreviousData: true,
    queryFn: () => axios.get<YearStatus[]>("/materials/years", {}).then((result) => result.data),
  });
}

export interface ProductRelationsPerYear {
  year: number | null;
  product_count: number;
}

export function useProductRelationsPerYear({ materialId }: { materialId: number }) {
  return useQuery({
    queryKey: ["materials", materialId, "related_products_per_year"],
    keepPreviousData: true,
    queryFn: () =>
      axios
        .get<ProductRelationsPerYear[]>(`/materials/${materialId}/related_products_per_year`)
        .then((result) => result.data),
  });
}
